import { motion } from "framer-motion";
import heroVideo from "../assests/office.mp4"; // 🎬 Add your background video here

export default function MultilingualTranscription() {
  return (
    <div className="bg-gray-50 text-gray-900 font-raleway">
      {/* Hero Section */}
      <section className="relative w-full h-[70vh] flex items-center justify-center text-center text-white">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          <source src={heroVideo} type="video/quicktime" />
          Your browser does not support the video tag.
        </video>

        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative z-10 px-6 max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6 drop-shadow-lg font-orbitron"
          >
            Breaking Language Barriers with Multilingual Transcription
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed text-gray-200"
          >
            Human-verified and AI-assisted transcription in 50+ languages and
            dialects for global reach and inclusivity.
          </motion.p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 text-gray-900 font-orbitron">
          Why Multilingual Transcription Matters
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          In today’s globalized world, businesses and technologies interact with
          audiences who speak hundreds of languages. Accurate transcription
          ensures accessibility, compliance, and inclusivity. Our services
          deliver precision, cultural sensitivity, and scalability to help your
          AI systems, platforms, and businesses thrive worldwide.
        </p>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-gray-100 via-white to-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 font-orbitron">
          Our Transcription Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            {
              title: "🎙 Audio & Video",
              desc: "Accurate transcription of spoken content across multiple languages.",
            },
            {
              title: "🌐 Multilingual & Dialects",
              desc: "Support for 50+ global languages and regional variations.",
            },
            {
              title: "🤖 AI + Human QA",
              desc: "Fast, scalable transcription with human-in-the-loop validation.",
            },
            {
              title: "📝 Time-Stamped",
              desc: "Synchronized transcripts ideal for subtitles and dubbing.",
            },
            {
              title: "🔒 Secure & Confidential",
              desc: "GDPR-compliant and enterprise-grade data protection.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition"
            >
              <h3 className="text-xl font-semibold mb-3 text-indigo-700 font-orbitron">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 font-orbitron">
          How We Work
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Receive & analyze audio/video files",
            "Apply speech recognition in multiple languages",
            "Human experts refine & validate transcripts",
            "Deliver clean, structured, and accurate data",
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true }}
              className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg"
            >
              <div className="text-4xl mb-3">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-indigo-50 via-white to-purple-50">
        <h2 className="text-4xl font-semibold text-center mb-12 font-orbitron">
          Applications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            {
              title: "🎧 Media & Entertainment",
              desc: "Subtitles, dubbing, and content localization.",
            },
            {
              title: "🏥 Healthcare",
              desc: "Medical transcription across regions for better care delivery.",
            },
            {
              title: "📚 E-Learning",
              desc: "Multilingual training materials and lectures.",
            },
            {
              title: "📞 Call Centers",
              desc: "Customer interaction transcripts for analytics and compliance.",
            },
            {
              title: "🤖 AI & NLP",
              desc: "Training data for multilingual chatbots and speech systems.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition"
            >
              <h3 className="font-semibold mb-2 text-lg text-indigo-700 font-orbitron">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 font-orbitron">
          Why Choose Us
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          ✅ Expertise in 50+ languages & dialects <br />
          ✅ Human-in-the-loop validation for maximum accuracy <br />
          ✅ GDPR & enterprise-grade security <br />
          ✅ Fast turnaround, scalable for global projects <br />
          ✅ Domain-specific expertise (medical, legal, business, etc.)
        </p>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 font-orbitron">
          Go Global with Accurate Multilingual Transcription
        </h2>
        <p className="text-lg max-w-xl mx-auto mb-6">
          Expand your reach, train smarter AI, and communicate seamlessly across
          languages with our transcription services.
        </p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-white text-indigo-700 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-gray-100 transition"
        >
          Contact Us
        </motion.button>
      </section>
    </div>
  );
}
